import { useMemo, useState } from 'react';
import {
  makeStyles,
  tokens,
  Body1,
  Body2,
  Caption1,
  Badge,
  Button,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Input,
  Textarea,
  Dropdown,
  Option,
  Field,
  Drawer,
  DrawerHeader,
  DrawerHeaderTitle,
  DrawerBody,
  Divider,
  MessageBar,
  MessageBarBody,
} from '@fluentui/react-components';
import { PageTitle } from '@/components/PageTitle';
import {
  Add24Regular,
  Dismiss24Regular,
  Delete24Regular,
} from '@fluentui/react-icons';
import { DataTable, type Column } from '@/components/DataTable';
import { useSharePointList } from '@/hooks/useSharePointList';
import type {
  Project,
  Allocation,
  Employee,
  Application,
  AppAssignment,
  CloudSpend,
  CostAllocation,
  ProjectStatus,
  SPListItem,
} from '@/models/types';
import { formatCurrency, formatDate, formatPercent } from '@/utils/formatters';
import { calculateProjectCost, type ProjectCost } from '@/utils/costCalculations';
import { PROJECT_STATUSES } from '@/utils/constants';
import { useAppContext } from '@/store/appContext';
import { canManageProjects, canEditProjectAllocations } from '@/utils/permissions';

const STATUS_COLORS: Record<ProjectStatus, 'success' | 'warning' | 'informative'> = {
  Active: 'success',
  'On Hold': 'warning',
  Completed: 'informative',
};

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: '20px' },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap' as const,
    gap: '12px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
    '@media (max-width: 480px)': {
      gridTemplateColumns: '1fr',
    },
  },
  allocationRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 0',
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
    gap: '12px',
  },
  addAllocationForm: {
    display: 'flex',
    gap: '8px',
    alignItems: 'flex-end',
    flexWrap: 'wrap' as const,
    padding: '12px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    '@media (max-width: 480px)': {
      flexDirection: 'column' as const,
      alignItems: 'stretch' as const,
      '& > *': {
        width: '100% !important',
        flex: 'none !important' as const,
      },
    },
  },
});

export function Projects() {
  const styles = useStyles();
  const { currentUser, listIds } = useAppContext();
  // Org-wide edit (create/edit any project) — admin/RO only
  const canEditAll = currentUser ? canManageProjects(currentUser.role) : false;
  // Per-project edit (allocations for one project) — admin/RO OR the project owner
  const canEditAllocations = (proj: SPListItem<Project> | null): boolean => {
    if (!currentUser || !proj) return false;
    return canEditProjectAllocations(
      currentUser.role,
      currentUser.email,
      proj.fields.OwnerEmail ?? '',
    );
  };
  // For the table-level Edit/Delete actions column — only admin/RO
  const canEdit = canEditAll;

  const { items: projects, loading, create, update, remove } =
    useSharePointList<Project>('OC_Projects', { autoFetch: !!listIds });
  const {
    items: allocations,
    create: createAlloc,
    update: updateAlloc,
    refresh: refreshAllocs,
  } = useSharePointList<Allocation>('OC_Allocations', {
    autoFetch: !!listIds,
  });
  const { items: employees } = useSharePointList<Employee>('OC_Employees', {
    autoFetch: !!listIds,
  });
  const { items: apps } = useSharePointList<Application>('OC_Applications', {
    autoFetch: !!listIds,
  });
  const { items: assignments } = useSharePointList<AppAssignment>(
    'OC_AppAssignments',
    { autoFetch: !!listIds },
  );
  const { items: cloudSpend } = useSharePointList<CloudSpend>('OC_CloudSpend', {
    autoFetch: !!listIds,
  });
  // Cost allocations — optional list, falls back gracefully if not provisioned.
  const { items: costAllocations } = useSharePointList<CostAllocation>(
    'OC_CostAllocations',
    { autoFetch: !!listIds?.OC_CostAllocations },
  );

  // Per-project cost (memoized so it recomputes only when data changes)
  const projectCosts = useMemo(() => {
    const map = new Map<string, ProjectCost>();
    for (const p of projects) {
      map.set(
        p.id,
        calculateProjectCost(
          p,
          allocations,
          employees,
          apps,
          assignments,
          cloudSpend,
          costAllocations,
        ),
      );
    }
    return map;
  }, [projects, allocations, employees, apps, assignments, cloudSpend, costAllocations]);

  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState<SPListItem<Project> | null>(null);
  const [selectedProject, setSelectedProject] = useState<SPListItem<Project> | null>(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    Title: '',
    Code: '',
    Description: '',
    Status: 'Active' as ProjectStatus,
    StartDate: '',
    EndDate: '',
    Budget: 0,
    OwnerEmail: '',
    AzureSubscriptionIds: '',
    GCPProjectIds: '',
  });

  // Allocation form state
  const [allocEmployeeId, setAllocEmployeeId] = useState('');
  const [allocRole, setAllocRole] = useState('');
  const [allocPercent, setAllocPercent] = useState('100');

  const openNewForm = () => {
    setFormData({
      Title: '',
      Code: '',
      Description: '',
      Status: 'Active',
      StartDate: new Date().toISOString().split('T')[0],
      EndDate: '',
      Budget: 0,
      OwnerEmail: currentUser?.email ?? '',
      AzureSubscriptionIds: '',
      GCPProjectIds: '',
    });
    setEditItem(null);
    setShowForm(true);
  };

  const openEditForm = (item: SPListItem<Project>) => {
    setFormData({ ...item.fields });
    setEditItem(item);
    setShowForm(true);
  };

  const handleSave = async () => {
    setError(null);
    if (!formData.Title || !formData.Code) {
      setError('Title and Code are required');
      return;
    }

    if (editItem) {
      await update(editItem.id, formData);
    } else {
      await create(formData);
    }
    setShowForm(false);
  };

  const handleDelete = async (item: SPListItem<Project>) => {
    if (confirm(`Delete project "${item.fields.Title}"?`)) {
      await remove(item.id);
    }
  };

  const handleAddAllocation = async () => {
    if (!selectedProject || !allocEmployeeId || !allocRole) return;
    await createAlloc({
      EmployeeId: Number(allocEmployeeId),
      ProjectId: Number(selectedProject.id),
      Role: allocRole,
      AllocationPercent: Number(allocPercent),
      StartDate: new Date().toISOString(),
      EndDate: '',
    });
    setAllocEmployeeId('');
    setAllocRole('');
    setAllocPercent('100');
    await refreshAllocs();
  };

  const filteredProjects =
    statusFilter === 'all'
      ? projects
      : projects.filter((p) => p.fields.Status === statusFilter);

  const getAllocationCount = (projectId: string) =>
    allocations
      .filter((a) => a.fields.ProjectId === Number(projectId))
      .filter((a) => isAllocationActive(a.fields)).length;

  // Active = on or before today, and either no EndDate or EndDate ≥ today.
  // Lets us keep ended rows in the list (for historical cost attribution)
  // without showing them as current members.
  const isAllocationActive = (a: { StartDate?: string; EndDate?: string }) => {
    const today = new Date().toISOString().slice(0, 10);
    return (
      (!a.StartDate || a.StartDate.slice(0, 10) <= today) &&
      (!a.EndDate || a.EndDate === '' || a.EndDate.slice(0, 10) >= today)
    );
  };

  const getProjectAllocations = (projectId: string) =>
    allocations
      .filter((a) => a.fields.ProjectId === Number(projectId))
      .filter((a) => isAllocationActive(a.fields));

  const getEmployeeName = (empId: number) =>
    employees.find((e) => Number(e.id) === empId)?.fields.Title ?? 'Unknown';

  // For dropdown - active employees not already allocated
  const availableEmployees = useMemo(() => {
    if (!selectedProject) return employees;
    const allocated = new Set(
      getProjectAllocations(selectedProject.id).map((a) => a.fields.EmployeeId),
    );
    return employees.filter(
      (e) => e.fields.Status === 'Active' && !allocated.has(Number(e.id)),
    );
  }, [employees, allocations, selectedProject]);

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = { all: projects.length };
    for (const s of PROJECT_STATUSES) {
      counts[s] = projects.filter((p) => p.fields.Status === s).length;
    }
    return counts;
  }, [projects]);

  const columns: Column<SPListItem<Project>>[] = [
    {
      key: 'code',
      label: 'Code',
      width: '100px',
      sortable: true,
      render: (item) => (
        <Badge appearance="outline" size="small">
          {item.fields.Code}
        </Badge>
      ),
      getValue: (item) => item.fields.Code,
    },
    {
      key: 'title',
      label: 'Project',
      sortable: true,
      getValue: (item) => item.fields.Title,
      render: (item) => <Body2 style={{ fontWeight: 500 }}>{item.fields.Title}</Body2>,
    },
    {
      key: 'status',
      label: 'Status',
      render: (item) => (
        <Badge
          appearance="filled"
          color={STATUS_COLORS[item.fields.Status]}
          size="small"
        >
          {item.fields.Status}
        </Badge>
      ),
      getValue: (item) => item.fields.Status,
      sortable: true,
    },
    {
      key: 'members',
      label: 'Members',
      render: (item) => (
        <Caption1>{getAllocationCount(item.id)} allocated</Caption1>
      ),
      getValue: (item) => getAllocationCount(item.id),
      sortable: true,
    },
    {
      key: 'budget',
      label: 'Budget',
      render: (item) => <Caption1>{formatCurrency(item.fields.Budget)}</Caption1>,
      getValue: (item) => item.fields.Budget,
      sortable: true,
    },
    {
      key: 'actualCost',
      label: 'Actual Cost/mo',
      render: (item) => {
        const cost = projectCosts.get(item.id);
        if (!cost) return <Caption1>—</Caption1>;
        return (
          <div>
            <Body2 style={{ fontWeight: 600 }}>
              {formatCurrency(cost.totalCost)}
            </Body2>
            <Caption1>
              Infra {formatCurrency(cost.infraCost)} · Lic {formatCurrency(cost.licenseCost)}
            </Caption1>
          </div>
        );
      },
      getValue: (item) => projectCosts.get(item.id)?.totalCost ?? 0,
      sortable: true,
    },
    {
      key: 'dates',
      label: 'Duration',
      render: (item) => (
        <Caption1>
          {formatDate(item.fields.StartDate)} — {formatDate(item.fields.EndDate)}
        </Caption1>
      ),
    },
    ...(canEdit
      ? [
          {
            key: 'actions',
            label: '',
            width: '80px',
            render: (item: SPListItem<Project>) => (
              <div style={{ display: 'flex', gap: 4 }}>
                <Button
                  appearance="subtle"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    openEditForm(item);
                  }}
                >
                  Edit
                </Button>
                <Button
                  appearance="subtle"
                  size="small"
                  icon={<Delete24Regular />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(item);
                  }}
                />
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <PageTitle>Projects</PageTitle>
      </div>

      {error && (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      <DataTable
        columns={columns}
        items={filteredProjects}
        loading={loading}
        searchPlaceholder="Search projects..."
        searchFields={(item) =>
          `${item.fields.Title} ${item.fields.Code} ${item.fields.Description}`
        }
        getRowKey={(item) => item.id}
        onRowClick={setSelectedProject}
        emptyMessage="No projects yet. Create one to get started."
        renderMobile={(item) => {
          const cost = projectCosts.get(item.id);
          return {
            title: item.fields.Title,
            subtitle: item.fields.Code + (item.fields.Description ? ` · ${item.fields.Description}` : ''),
            meta: cost
              ? `${cost.memberCount} members · ${formatCurrency(cost.totalCost)}/mo`
              : item.fields.Status,
            trailing: (
              <Badge
                appearance="filled"
                size="small"
                color={
                  item.fields.Status === 'Active'
                    ? 'success'
                    : item.fields.Status === 'On Hold'
                      ? 'warning'
                      : 'informative'
                }
              >
                {item.fields.Status}
              </Badge>
            ),
          };
        }}
        filterTabs={[
          { key: 'all', label: 'All', count: statusCounts.all },
          ...PROJECT_STATUSES.map((s) => ({
            key: s,
            label: s,
            count: statusCounts[s] ?? 0,
          })),
        ]}
        activeFilter={statusFilter}
        onFilterChange={setStatusFilter}
        actions={
          canEdit ? (
            <Button
              appearance="primary"
              icon={<Add24Regular />}
              onClick={openNewForm}
            >
              New Project
            </Button>
          ) : undefined
        }
      />

      {/* Create/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={() => setShowForm(false)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>
              {editItem ? 'Edit Project' : 'New Project'}
            </DialogTitle>
            <DialogContent>
              <div className={styles.form}>
                <div className={styles.row}>
                  <Field label="Title" required>
                    <Input
                      value={formData.Title}
                      onChange={(_, d) =>
                        setFormData((f) => ({ ...f, Title: d.value }))
                      }
                    />
                  </Field>
                  <Field label="Code" required>
                    <Input
                      value={formData.Code}
                      onChange={(_, d) =>
                        setFormData((f) => ({ ...f, Code: d.value }))
                      }
                    />
                  </Field>
                </div>
                <Field label="Description">
                  <Textarea
                    value={formData.Description}
                    onChange={(_, d) =>
                      setFormData((f) => ({ ...f, Description: d.value }))
                    }
                    resize="vertical"
                  />
                </Field>
                <div className={styles.row}>
                  <Field label="Status">
                    <Dropdown
                      value={formData.Status}
                      onOptionSelect={(_, d) =>
                        setFormData((f) => ({
                          ...f,
                          Status: d.optionValue as ProjectStatus,
                        }))
                      }
                    >
                      {PROJECT_STATUSES.map((s) => (
                        <Option key={s} value={s}>
                          {s}
                        </Option>
                      ))}
                    </Dropdown>
                  </Field>
                  <Field label="Budget (GBP)">
                    <Input
                      type="number"
                      value={String(formData.Budget)}
                      onChange={(_, d) =>
                        setFormData((f) => ({
                          ...f,
                          Budget: Number(d.value) || 0,
                        }))
                      }
                    />
                  </Field>
                </div>
                <div className={styles.row}>
                  <Field label="Start Date">
                    <Input
                      type="date"
                      value={formData.StartDate?.split('T')[0] ?? ''}
                      onChange={(_, d) =>
                        setFormData((f) => ({ ...f, StartDate: d.value }))
                      }
                    />
                  </Field>
                  <Field label="End Date">
                    <Input
                      type="date"
                      value={formData.EndDate?.split('T')[0] ?? ''}
                      onChange={(_, d) =>
                        setFormData((f) => ({ ...f, EndDate: d.value }))
                      }
                    />
                  </Field>
                </div>
                <Field label="Owner Email">
                  <Input
                    value={formData.OwnerEmail}
                    onChange={(_, d) =>
                      setFormData((f) => ({ ...f, OwnerEmail: d.value }))
                    }
                  />
                </Field>
                <Field
                  label="Azure Subscription IDs (comma-separated)"
                  hint="Attribute Azure spend to this project"
                >
                  <Textarea
                    value={formData.AzureSubscriptionIds}
                    onChange={(_, d) =>
                      setFormData((f) => ({ ...f, AzureSubscriptionIds: d.value }))
                    }
                    placeholder="00000000-0000-0000-0000-000000000000"
                    rows={2}
                  />
                </Field>
                <Field
                  label="GCP Project IDs (comma-separated)"
                  hint="Attribute GCP spend to this project"
                >
                  <Textarea
                    value={formData.GCPProjectIds}
                    onChange={(_, d) =>
                      setFormData((f) => ({ ...f, GCPProjectIds: d.value }))
                    }
                    placeholder="my-gcp-project-id, another-project"
                    rows={2}
                  />
                </Field>
              </div>
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary">Cancel</Button>
              </DialogTrigger>
              <Button appearance="primary" onClick={handleSave}>
                {editItem ? 'Save Changes' : 'Create Project'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Project Detail Drawer */}
      <Drawer
        type="overlay"
        position="end"
        open={!!selectedProject}
        onOpenChange={() => setSelectedProject(null)}
        size="medium"
      >
        {selectedProject && (
          <>
            <DrawerHeader>
              <DrawerHeaderTitle
                action={
                  <Button
                    appearance="subtle"
                    icon={<Dismiss24Regular />}
                    onClick={() => setSelectedProject(null)}
                  />
                }
              >
                {selectedProject.fields.Title}
              </DrawerHeaderTitle>
            </DrawerHeader>
            <DrawerBody>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                <Badge appearance="outline">{selectedProject.fields.Code}</Badge>
                <Caption1>{selectedProject.fields.Description}</Caption1>
                <Caption1>
                  {formatDate(selectedProject.fields.StartDate)} —{' '}
                  {formatDate(selectedProject.fields.EndDate)}
                </Caption1>
                <Caption1>
                  Budget: {formatCurrency(selectedProject.fields.Budget)}
                </Caption1>
              </div>

              <Divider style={{ margin: '12px 0' }} />

              {(() => {
                const cost = projectCosts.get(selectedProject.id);
                if (!cost) return null;
                return (
                  <div
                    style={{
                      padding: 12,
                      backgroundColor: tokens.colorNeutralBackground2,
                      borderRadius: tokens.borderRadiusMedium,
                      marginBottom: 12,
                    }}
                  >
                    <Body1 style={{ fontWeight: 600, marginBottom: 8 }}>
                      Monthly cost breakdown
                    </Body1>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 6 }}>
                      <Caption1>Infrastructure (cloud spend)</Caption1>
                      <Caption1 style={{ textAlign: 'right', fontWeight: 500 }}>
                        {formatCurrency(cost.infraCost)}
                      </Caption1>
                      <Caption1>
                        Licenses (weighted by allocation %)
                      </Caption1>
                      <Caption1 style={{ textAlign: 'right', fontWeight: 500 }}>
                        {formatCurrency(cost.licenseCost)}
                      </Caption1>
                      <Body2 style={{ fontWeight: 700, marginTop: 4 }}>Total</Body2>
                      <Body2
                        style={{
                          textAlign: 'right',
                          fontWeight: 700,
                          marginTop: 4,
                          color: tokens.colorBrandForeground1,
                        }}
                      >
                        {formatCurrency(cost.totalCost)}
                      </Body2>
                    </div>
                    {cost.subscriptionIds.length > 0 ? (
                      <Caption1
                        style={{
                          display: 'block',
                          marginTop: 8,
                          color: tokens.colorNeutralForeground3,
                        }}
                      >
                        Linked subscriptions: {cost.subscriptionIds.length}
                      </Caption1>
                    ) : (
                      <Caption1
                        style={{
                          display: 'block',
                          marginTop: 8,
                          color: tokens.colorPaletteYellowForeground1,
                        }}
                      >
                        No Azure subscriptions linked — edit project to add.
                      </Caption1>
                    )}
                  </div>
                );
              })()}

              <Divider />

              <Body1 style={{ fontWeight: 600, margin: '16px 0 12px' }}>
                Member Allocations
              </Body1>

              {(() => {
                // Group allocations by team (derived from Role prefix before " - " or first word)
                const allocs = getProjectAllocations(selectedProject.id);
                const groups = new Map<string, typeof allocs>();
                for (const a of allocs) {
                  const role = a.fields.Role ?? '';
                  // Team = text before " - " if present, else first word block
                  const team =
                    role.includes(' - ')
                      ? role.split(' - ')[0].trim()
                      : role.split(/\s+/)[0] || 'Team';
                  const list = groups.get(team) ?? [];
                  list.push(a);
                  groups.set(team, list);
                }
                const sortedTeams = Array.from(groups.keys()).sort();

                return (
                  <>
                    <Caption1 style={{ display: 'block', marginBottom: 8 }}>
                      {allocs.length} members across {sortedTeams.length} team{sortedTeams.length !== 1 ? 's' : ''}
                    </Caption1>
                    {sortedTeams.map((team) => {
                      const teamAllocs = groups.get(team)!;
                      return (
                        <div
                          key={team}
                          style={{
                            marginBottom: 12,
                            backgroundColor: tokens.colorNeutralBackground2,
                            borderRadius: tokens.borderRadiusMedium,
                            padding: 10,
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              marginBottom: 6,
                            }}
                          >
                            <Body2 style={{ fontWeight: 600 }}>{team}</Body2>
                            <Badge appearance="outline" size="small">
                              {teamAllocs.length}
                            </Badge>
                          </div>
                          {teamAllocs
                            .slice()
                            .sort((a, b) =>
                              getEmployeeName(a.fields.EmployeeId).localeCompare(
                                getEmployeeName(b.fields.EmployeeId),
                              ),
                            )
                            .map((alloc) => (
                              <div
                                key={alloc.id}
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  padding: '4px 0',
                                  gap: 8,
                                }}
                              >
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <Body2
                                    style={{
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {getEmployeeName(alloc.fields.EmployeeId)}
                                  </Body2>
                                  <Caption1>
                                    {alloc.fields.Role}
                                    {alloc.fields.StartDate && (
                                      <span
                                        style={{
                                          marginLeft: 6,
                                          color: tokens.colorNeutralForeground3,
                                          fontFamily:
                                            '"SF Mono", ui-monospace, Menlo, monospace',
                                          fontSize: '11px',
                                        }}
                                      >
                                        · since {formatDate(alloc.fields.StartDate)}
                                      </span>
                                    )}
                                  </Caption1>
                                </div>
                                <Badge
                                  appearance={
                                    alloc.fields.AllocationPercent === 0
                                      ? 'tint'
                                      : 'filled'
                                  }
                                  color={
                                    alloc.fields.AllocationPercent === 0
                                      ? 'informative'
                                      : alloc.fields.AllocationPercent >= 100
                                        ? 'success'
                                        : 'brand'
                                  }
                                  size="small"
                                >
                                  {formatPercent(alloc.fields.AllocationPercent)}
                                </Badge>
                                {canEditAllocations(selectedProject) && (
                                  <Button
                                    appearance="subtle"
                                    size="small"
                                    icon={<Delete24Regular />}
                                    title="End allocation today (preserves cost history)"
                                    onClick={async () => {
                                      const today = new Date()
                                        .toISOString()
                                        .slice(0, 10);
                                      const ok = window.confirm(
                                        `End ${getEmployeeName(alloc.fields.EmployeeId)}'s allocation to this project today?\n\n` +
                                          `Their share of license cost will continue to be attributed here for dates up to today, ` +
                                          `then move to whichever project you allocate them to next. Pick "Cancel" if you want to ` +
                                          `delete the allocation entirely instead (loses history).`,
                                      );
                                      if (!ok) return;
                                      // Soft end: keep the row, mark when it ended.
                                      await updateAlloc(alloc.id, {
                                        EndDate: today,
                                      });
                                      await refreshAllocs();
                                    }}
                                  />
                                )}
                              </div>
                            ))}
                        </div>
                      );
                    })}
                  </>
                );
              })()}

              {canEditAllocations(selectedProject) && (
                <>
                  <Divider style={{ margin: '16px 0' }} />
                  <Body1 style={{ fontWeight: 600, marginBottom: 12 }}>
                    Add Member
                  </Body1>
                  <div className={styles.addAllocationForm}>
                    <Field label="Employee" style={{ flex: 2 }}>
                      <Dropdown
                        value={
                          availableEmployees.find(
                            (e) => e.id === allocEmployeeId,
                          )?.fields.Title ?? ''
                        }
                        onOptionSelect={(_, d) =>
                          setAllocEmployeeId(d.optionValue as string)
                        }
                        placeholder="Select employee"
                      >
                        {availableEmployees.map((e) => (
                          <Option key={e.id} value={e.id}>
                            {e.fields.Title}
                          </Option>
                        ))}
                      </Dropdown>
                    </Field>
                    <Field label="Role" style={{ flex: 1 }}>
                      <Input
                        value={allocRole}
                        onChange={(_, d) => setAllocRole(d.value)}
                        placeholder="Developer"
                      />
                    </Field>
                    <Field label="%" style={{ width: 80 }}>
                      <Input
                        type="number"
                        value={allocPercent}
                        onChange={(_, d) => setAllocPercent(d.value)}
                      />
                    </Field>
                    <Button
                      appearance="primary"
                      onClick={handleAddAllocation}
                      disabled={!allocEmployeeId || !allocRole}
                      style={{ alignSelf: 'flex-end' }}
                    >
                      Add
                    </Button>
                  </div>
                </>
              )}
            </DrawerBody>
          </>
        )}
      </Drawer>
    </div>
  );
}
